import React, { useState, useEffect } from 'react';
import { Table, notification, ConfigProvider, theme, Dropdown, Button as AntdButton, Modal } from 'antd';
import { ArrowLeftOutlined, CheckCircleOutlined, SearchOutlined, FilterOutlined, MoreOutlined, ExclamationCircleFilled, SafetyCertificateOutlined } from '@ant-design/icons';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Header from "./Header";
import AdminSidebar from "./Sidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { db, functions, auth } from "../firebase/config";
import { collection, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

interface ManagePrivilegesProps {
  onBack: () => void;
  schoolID: string;
}

interface User {
  id: string;
  employeeId: string;
  name: string;
  email: string;
  role: 'Admin' | 'School Staff' | 'Assistant Admin';
  department: string;
  status: 'Active' | 'Inactive';
}

const ManagePrivileges: React.FC<ManagePrivilegesProps> = ({ onBack, schoolID }) => {
  /* State */
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [api, contextHolder] = notification.useNotification();
  
  // Declarative Modal State
  const [confirmationData, setConfirmationData] = useState<{ 
    userEmail: string; 
    userName: string; 
    currentRole: string; 
    targetRole: User['role']; 
  } | null>(null);

  // Fetch Teachers from Firebase
  useEffect(() => {
    if (!schoolID) return;

    const teachersRef = collection(db, `schools/${schoolID}/teachers`);
    const unsubscribe = onSnapshot(teachersRef, (snapshot) => {
      const fetchedUsers: User[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          employeeId: data.employeeId || 'N/A',
          name: `${data.firstName || ''} ${data.lastName || ''}`.trim(),
          email: data.email || 'N/A',
          role: data.role === 'admin' ? 'Admin' : data.role === 'assistant_admin' ? 'Assistant Admin' : 'School Staff',
          department: Array.isArray(data.teacherType) ? data.teacherType.join(", ") : (data.teacherType || 'Academic'), // Map teacherType to department
          status: data.suspended ? 'Inactive' : 'Active',
        };
      });
      setUsers(fetchedUsers);
    });

    return () => unsubscribe();
  }, [schoolID]);

  const handleRoleChange = async (userEmail: string, newRole: 'Admin' | 'School Staff' | 'Assistant Admin') => {
    // 1. Close Modal Immediately
    setConfirmationData(null);
    
    // 2. Map Role for Backend
    // Backend expects: 'admin', 'school_staff', or 'assistant_admin'
    let backendRole = 'school_staff';
    if (newRole === 'Admin') backendRole = 'admin';
    else if (newRole === 'Assistant Admin') backendRole = 'assistant_admin';
    
    // 3. Show Loading
    api.info({
      message: 'Updating Access...',
      description: 'Please wait while we update user privileges.',
      placement: 'bottomRight',
      className: 'dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
      duration: 0, // Keep open until done
      key: 'update-role-loading', // Unique key to update/close later
    });

    try {
        const updateSchoolRole = httpsCallable(functions, 'updateSchoolRole');
        await updateSchoolRole({
            targetUserEmail: userEmail,
            newRole: backendRole,
            schoolID
        });

        // 4. Handle Success
        api.destroy('update-role-loading'); // Remove loading
        api.success({
            message: 'Access Updated',
            description: 'Permissions modified successfully.',
            placement: 'bottomRight',
            className: 'dark:bg-gray-800 dark:text-white dark:border-gray-700',
            icon: <CheckCircleOutlined style={{ color: '#10B981' }} />,
            duration: 3,
        });

    } catch (error: any) {
        console.error("Role update failed:", error);
        api.destroy('update-role-loading'); // Remove loading
        
        let errorMessage = 'Could not update user privileges.';
        let errorDescription = error.message;

        if (error.message && error.message.includes('no user record')) {
            errorMessage = 'Account Not Found';
            errorDescription = 'The user account associated with this record seems to have been deleted from the authentication system.';
        }
        
        api.error({
            message: errorMessage,
            description: errorDescription,
            placement: 'bottomRight',
            className: 'dark:bg-red-900/20 dark:text-red-200 dark:border-red-900',
            duration: 5,
        });
    }
  };

  const confirmRoleChange = (userId: string, targetRole: User['role']) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    setConfirmationData({
        userEmail: user.email,
        userName: user.name,
        currentRole: user.role,
        targetRole
    });
  };

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.employeeId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    {
      title: '#',
      key: 'index',
      width: '50px',
      responsive: ['md'] as any,
      render: (_: any, __: any, index: number) => (
        <span className="text-xs text-gray-400 font-mono">{index + 1}</span>
      ),
    },
    {
      title: 'EMPLOYEE',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
      render: (text: string, record: User) => {
        const isCurrentUser = record.email === auth.currentUser?.email;
        return (
          <div className="flex flex-col min-w-0">
            <span className="font-medium text-gray-900 dark:text-gray-200 text-sm truncate">
              {text} 
              {isCurrentUser && <span className="ml-2 text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 px-1.5 py-0.5 rounded-full font-semibold">You</span>}
            </span>
            <span className={`text-[11px] truncate ${isCurrentUser ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-500 dark:text-gray-500'}`}>
              {record.email}
            </span>
          </div>
        );
      },
    },
    {
      title: 'ID',
      dataIndex: 'employeeId',
      key: 'employeeId',
      width: '10%',
      render: (id: string) => (
        <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
          {id}
        </span>
      ),
    },
    {
      title: 'DEPT',
      dataIndex: 'department',
      key: 'department',
      width: '18%',
      responsive: ['lg'] as any,
      render: (text: string) => (
        <span className="text-xs text-gray-600 dark:text-gray-400">
          {text}
        </span>
      ),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      width: '12%',
      responsive: ['sm'] as any,
      render: (status: string) => (
        <div className="flex items-center gap-1.5">
          <div className={`w-1.5 h-1.5 rounded-full ${status === 'Active' ? 'bg-emerald-500' : 'bg-gray-400'}`} />
          <span className="text-[11px] text-gray-600 dark:text-gray-400">{status}</span>
        </div>
      ),
    },
    {
      title: 'ROLE',
      dataIndex: 'role',
      key: 'role',
      width: '20%',
      render: (role: string) => {
        let style = "";
        if (role === 'Admin') style = "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-900/50";
        else if (role === 'Assistant Admin') style = "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-900/50";
        else style = "bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700";

        return (
          <div className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${style}`}>
            {role}
          </div>
        );
      },
    },
    {
      title: 'ACTION',
      key: 'action',
      width: '20%',
      render: (_: any, record: User) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'admin',
                  label: 'Set as Admin',
                  onClick: () => confirmRoleChange(record.id, 'Admin'),
                  disabled: record.role === 'Admin',
                },
                {
                  key: 'assistant',
                  label: 'Set as Assistant Admin',
                  onClick: () => confirmRoleChange(record.id, 'Assistant Admin'),
                  disabled: record.role === 'Assistant Admin',
                },
                {
                  key: 'staff',
                  label: 'Set as School Staff',
                  onClick: () => confirmRoleChange(record.id, 'School Staff'),
                  disabled: record.role === 'School Staff',
                },
              ],
            }}
            trigger={['click']}
          >
            <AntdButton
              size="small"
              className="action-btn h-8 w-8"
            >
              <MoreOutlined />
            </AntdButton>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <ConfigProvider
        theme={{
            algorithm: theme.defaultAlgorithm,
            token: {
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                colorPrimary: '#2563EB',
                borderRadius: 6, 
                fontSize: 14, 
                colorBgContainer: 'var(--bg-container)',
                colorText: 'var(--text-primary)',
                colorBorder: 'var(--border-color)',
            },
            components: {
                Table: {
                    headerBg: 'var(--header-bg)',
                    headerColor: 'var(--text-tertiary)',
                    headerSplitColor: 'transparent',
                    rowHoverBg: 'var(--row-hover)',

                    borderColor: 'var(--border-color)',
                    cellPaddingBlock: 12, 
                    cellPaddingInline: 16,
                    fontSize: 14, 
                },
                Modal: {
                    contentBg: 'var(--bg-container)',
                    headerBg: 'var(--bg-container)',
                    titleColor: 'var(--text-primary)',
                    fontSizeHeading5: 16,
                },
                Select: {
                  colorBgContainer: 'var(--input-bg)',
                  controlItemBgHover: 'var(--row-hover)',
                  fontSize: 14,
                },
                Button: {
                  fontSize: 14,
                }
            }
        }}
      >
        <style>{`
          :root {
            --bg-container: #ffffff;
            --header-bg: #F9FAFB;
            --text-primary: #1F2937;
            --text-secondary: #4B5563;
            --text-tertiary: #6B7280;
            --border-color: #E5E7EB;
            --row-hover: #F9FAFB;
            --input-bg: #ffffff;
          }
          .dark {
            --bg-container: #0B1120; /* Matching Main Card */
            --header-bg: #0F172A;
            --text-primary: #F3F4F6;
            --text-secondary: #9CA3AF;
            --text-tertiary: #6B7280;
            --border-color: #1E293B;
            --row-hover: #1E293B;
            --input-bg: #1E2937;
          }
          
          /* Refined Table Headers */
          .ant-table-thead > tr > th {
             font-size: 11px !important; 
             text-transform: uppercase;
             letter-spacing: 0.05em;
             font-weight: 700 !important;
             border-bottom: 2px solid var(--border-color) !important;
             background: var(--header-bg) !important;
          }

          /* Dropdown / Menu Overrides for Theme Consistency */
          .ant-dropdown-menu {
             background-color: var(--bg-container) !important;
             border: 1px solid var(--border-color) !important;
             padding: 4px !important;
             box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.5) !important;
          }
          .ant-dropdown-menu-item {
             color: var(--text-primary) !important;
             border-radius: 4px !important;
             padding: 8px 12px !important;
             font-size: 13px !important;
          }
          .ant-dropdown-menu-item:hover {
             background-color: var(--row-hover) !important;
          }
          .ant-dropdown-menu-item-disabled {
             color: var(--text-tertiary) !important;
             opacity: 0.5;
             background: transparent !important;
          }

          /* Pagination */
          .ant-pagination {
             font-size: 13px !important;
          }

          /* Input overrides */
          .compact-input {
             font-size: 14px;
             height: 38px; 
          }

          /* Action Button Styling */
          .action-btn {
             border: 1px solid var(--border-color) !important;
             background: var(--bg-container) !important;
             color: var(--text-secondary) !important;
             display: flex !important;
             align-items: center !important;
             justify-content: center !important;
             transition: all 0.2s !important;
          }
          .action-btn:hover:not(:disabled) {
             border-color: #2563EB !important;
             color: #2563EB !important;
             background: var(--row-hover) !important;
          }

          /* Modal Specifics */
          .custom-modal .ant-modal-content {
            border: 1px solid var(--border-color) !important;
            padding: 0 !important;
            overflow: hidden;
            border-radius: 12px !important;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5) !important;
          }
          .custom-modal .ant-modal-close {
            color: var(--text-secondary) !important;
          }
          .custom-modal .ant-modal-close:hover {
             color: var(--text-primary) !important;
          }
        `}</style>

        <div className="fixed inset-0 bg-gray-50 dark:bg-[#020817] overflow-hidden">
            <SidebarProvider className="h-full overflow-hidden">
                <div className="flex h-full w-full">
                    <AdminSidebar onSectionChange={() => {}} />
                    <div className="flex flex-col h-full flex-1 min-w-0">
                        <Header />
                        <main className="flex-1 min-h-0 overflow-auto p-4 sm:p-5">
                            
                            <div className="max-w-5xl mx-auto space-y-4">
                                {/* Compact Navigation & Header */}
                                <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-4 flex-wrap gap-4">
                                    <div className="flex items-center gap-3">
                                         <Button 
                                            variant="ghost" 
                                            size="icon"
                                            className="h-8 w-8 p-0 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
                                            onClick={onBack}
                                        >
                                            <ArrowLeftOutlined className="w-4 h-4" />
                                        </Button>
                                        <div>
                                            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white leading-tight">Privilege Management</h2>
                                            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">System Access Control</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Main Content Card */}
                                <div className="bg-white dark:bg-[#0B1120] rounded-lg border border-gray-200 dark:border-gray-800 shadow-sm min-h-[500px] flex flex-col">
                                    
                                    {/* Toolbar - Centered Search */}
                                    <div className="relative px-4 py-4 border-b border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row items-center gap-4 bg-gray-50/30 dark:bg-[#0B1120]">
                                        <div className="w-full sm:flex-1 flex justify-center order-2 sm:order-1">
                                            <div className="relative w-full max-w-md">
                                                <SearchOutlined className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                                                <Input 
                                                    placeholder="Filter users..." 
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    className="compact-input pl-9 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 focus:ring-1 focus:ring-blue-500 shadow-none rounded-md w-full"
                                                />
                                            </div>
                                        </div>
                                        <div className="w-full sm:w-auto flex justify-end order-1 sm:order-2">
                                            <Button variant="outline" size="sm" className="h-9 text-xs sm:text-sm gap-2 px-4 bg-white dark:bg-transparent dark:border-gray-700 w-full sm:w-auto">
                                                <FilterOutlined className="w-4 h-4" /> Filter
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Table Content */}
                                    <div className="flex-1 relative">
                                        <div className="w-full">
                                            <Table
                                                columns={columns}
                                                dataSource={filteredUsers}
                                                rowKey="id"
                                                pagination={{ 
                                                    pageSize: 10, 
                                                    size: "small",
                                                    className: "compact-pagination py-4"
                                                }}
                                                className="w-full"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </main>
                    </div>
                </div>
            </SidebarProvider>
            
            {/* Declarative Custom Modal */}
            <Modal
                title={null}
                footer={null}
                open={!!confirmationData}
                onCancel={() => setConfirmationData(null)}
                centered
                width="95%"
                style={{ maxWidth: '420px' }}
                className="custom-modal"
                closeIcon={<span className="text-xl">×</span>}
            >
                <div>
                   {/* Header Area */}
                   <div className="px-6 pt-6 pb-2">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400">
                                <ExclamationCircleFilled style={{ fontSize: '20px' }} />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white leading-tight">Confirm Changes</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Update system access privileges</p>
                            </div>
                        </div>
                   </div>

                   {/* Body Content */}
                   <div className="px-6 py-4">
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-5 leading-relaxed">
                            You are changing access rights for <span className="font-semibold text-gray-900 dark:text-white">{confirmationData?.userName}</span>.
                            This will immediately affect their ability to manage system resources.
                        </p>

                        <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-lg p-4 flex items-center justify-between">
                            <div className="flex flex-col gap-1">
                                <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Previous Role</span>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{confirmationData?.currentRole}</span>
                            </div>
                            
                            <ArrowLeftOutlined className="rotate-180 text-gray-300 dark:text-gray-600" />
                            
                            <div className="flex flex-col gap-1 items-end">
                                <span className="text-[10px] uppercase font-bold text-blue-500 tracking-wider">New Role</span>
                                <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{confirmationData?.targetRole}</span>
                            </div>
                        </div>

                        {confirmationData?.targetRole === 'Admin' && (
                            <div className="mt-5 p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/20 rounded-lg">
                                <div className="flex gap-3">
                                    <SafetyCertificateOutlined className="text-amber-600 dark:text-amber-500 text-lg flex-shrink-0 mt-0.5" />
                                    <p className="text-[13px] text-amber-800 dark:text-amber-400/90 leading-relaxed">
                                        You are granting <strong>Full Administrative Control</strong>. This user will have the exact same privileges as your account, including the ability to manage other users and system settings.
                                    </p>
                                </div>
                            </div>
                        )}
                   </div>

                   {/* Footer Actions */}
                   <div className="px-6 pb-6 pt-2 flex gap-3 justify-center">
                        <Button 
                            variant="outline" 
                            onClick={() => setConfirmationData(null)}
                            className="h-10 min-w-[120px] hover:bg-gray-100 dark:hover:bg-gray-800 dark:text-gray-300 dark:border-gray-700"
                        >
                            Cancel
                        </Button>
                        <Button 
                            className="h-10 min-w-[140px] bg-blue-600 hover:bg-blue-700 text-white border-0"
                            onClick={() => confirmationData && handleRoleChange(confirmationData.userEmail, confirmationData.targetRole)}
                        >
                            Confirm Update
                        </Button>
                   </div>
                </div>
            </Modal>
        </div>
      </ConfigProvider>
    </>
  );
};

export default ManagePrivileges;
